import { Box, Building, ChevronDown, Clipboard, Home as HomeIcon, Layers, Lightbulb, Shield, Warehouse, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavbar } from '../components/NavbarContext';
import { pdfToImage } from '../Utility/pdfUtils';
import './Home.css';

import HowItWorksSection from './HowItWorksSection';

import { API_URL } from '../config';
import PromptValidationModal from './Playground/components/Promptvalidationmodal';
import { getValidAccessToken } from '../Utility/authUtils';
import UploadPlanModal from './Playground/components/UploadPlanModal';

const FEET_TO_UNITS = 10;

function scalePlanGeometryForRender<T = any>(plan: T, scale = FEET_TO_UNITS): T {
  const isPoint = (v: any) =>
    v && typeof v === "object" && typeof v.x === "number" && typeof v.z === "number";

  const visit = (node: any): any => {
    if (Array.isArray(node)) return node.map(visit);
    if (node && typeof node === "object") {
      if (isPoint(node)) return { x: node.x * scale, z: node.z * scale };
      const out: any = {};
      for (const k of Object.keys(node)) out[k] = visit(node[k]);
      return out;
    }
    return node;
  };

  return visit(plan);
}

const Home: React.FC = () => {
  const { isProfileDropdownOpen } = useNavbar();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark";
  });

  // Sync with theme changes
  useEffect(() => {
    const checkTheme = () => {
      const savedTheme = localStorage.getItem("theme");
      setIsDarkMode(savedTheme === "dark");
    };

    checkTheme();
    window.addEventListener("storage", checkTheme);
    const interval = setInterval(checkTheme, 100);

    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  const [prompt, setPrompt] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isProcessingPdf, setIsProcessingPdf] = useState<boolean>(false);

  // Dynamic placeholder state
  const [placeholderText, setPlaceholderText] = useState<string>("");
  const [placeholderIndex, setPlaceholderIndex] = useState<number>(0);
  const [charIndex, setCharIndex] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [showValidationModal, setShowValidationModal] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');
  const [validationSuggestions, setValidationSuggestions] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [currentProjectId, setCurrentProjectId] = useState<string>('');
  const placeholders = [
    "Describe your floor plan requirements...",
    "I'm planning a 3 bedroom home with an open kitchen...",
    "Generate a 4 bedroom house with separate living spaces...",
    "I want a modern layout with lots of natural light...",
    "Create a compact 2 bedroom plan with a balcony...",
    "Design a split-level home with a home office..."
  ];

  const quickPrompts = [
      {
      label: "Office Plan",
      icon: <Building size={16} />,
      prompts: [
        "Design a 2500 sq.ft modern open office with a 35x20 ft collaborative workspace, 6-person conference room, CEO cabin (12x10 ft), two manager cabins, pantry with coffee bar, restrooms, and reception area.",
        "Create a 1800 sq.ft startup office with hot-desking for 15 people, one glass-walled meeting room (14x12 ft), phone booth, breakout zone with lounge seating, kitchenette, and storage closet.",
        "Plan a 3200 sq.ft executive office suite with private offices for 4 partners (each 12x10 ft), central secretarial station, 20x14 ft boardroom, waiting lounge, server room, and executive restroom.",
        "Design a 1200 sq.ft compact co-working space with 8 dedicated desks, one shared meeting room (10x10 ft), quiet work pods, small pantry, printer station, and communal seating area near entrance."
      ]
    },
    {
      label: "Industrial Warehouse",
      icon: <Warehouse size={16} />,
      prompts: [
        "Design a 5000 sq.ft industrial warehouse conversion with 20 ft ceilings, a 40x30 ft open floor production area, loading dock access, 15x12 ft office space, restroom facilities, and overhead crane provisions.",
        "Create a 3500 sq.ft compact warehouse with 18 ft clear height, 35x25 ft storage zone with pallet racking, 12x10 ft office corner, two loading bays, employee break room, and industrial lighting.",
        "Plan a 7000 sq.ft multi-zone warehouse with 24 ft ceilings, 50x40 ft main storage area, 20x15 ft workshop section, 15x12 ft climate-controlled room, shipping/receiving area, and mezzanine office overlooking floor.",
        "Design a 4200 sq.ft flexible warehouse with 16 ft height, 45x30 ft open space, drive-in door, side office suite (12x10 ft), utility room, and separate pedestrian entrance with small reception."
      ]
    },    
    {
      label: "Design a 3BHK House",
      icon: <HomeIcon size={16} />,
      prompts: [
        "Design a modern 3BHK on a 40x60 ft plot with ~1800 sq.ft built-up area, an 18x14 ft living room, a 12x10 ft kitchen near dining, a 14x12 ft master bedroom with attached bath, two 12x11 ft bedrooms, and a well-lit balcony.",
        "Create a compact 3BHK for a 30x40 ft plot with ~1000 sq.ft area, a 14x12 ft living space, 10x8 ft kitchen, three efficiently sized bedrooms, two 7x5 ft bathrooms, and good cross-ventilation.",
        "Plan a premium 3BHK apartment of ~2200 sq.ft with a large open living-dining, a 16x14 ft master suite, two spacious bedrooms, a small study, and a wide balcony with full-height windows.",
        "Design a traditional 3BHK around a central courtyard on a 50x50 ft site, with a 18x14 ft living room, surrounding bedrooms, a front veranda, and a separate service zone."
      ]
    },
  ];


  const handleQuickPrompt = (prompts: string[]) => {
    const randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    setPrompt(randomPrompt);
  };

  // Dynamic typing effect for placeholder
  useEffect(() => {
    if (prompt) return; // Don't animate if user is typing

    const currentPlaceholder = placeholders[placeholderIndex];
    const typingSpeed = isDeleting ? 30 : 80;
    const pauseTime = isDeleting ? 500 : 2500;

    const timer = setTimeout(() => {
      if (!isDeleting && charIndex < currentPlaceholder.length) {
        setPlaceholderText(currentPlaceholder.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      } else if (isDeleting && charIndex > 0) {
        setPlaceholderText(currentPlaceholder.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      } else if (!isDeleting && charIndex === currentPlaceholder.length) {
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && charIndex === 0) {
        setIsDeleting(false);
        setPlaceholderIndex((placeholderIndex + 1) % placeholders.length);
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, placeholderIndex, prompt]);

  // Check for saved state on mount
  useEffect(() => {
    const savedPrompt = localStorage.getItem("pending_prompt");
    const savedImage = localStorage.getItem("pending_image");

    if (savedPrompt) {
      setPrompt(savedPrompt);
      localStorage.removeItem("pending_prompt");
    }

    // if (savedImage) {
    //   setImage(savedImage);
    //   localStorage.removeItem("pending_image");
    // }
  }, []);

  // const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   // ... (keep handleImageUpload same)
  //   const files = e.target.files;
  //   if (files && files.length > 0) {
  //     const file = files[0];

  //     try {
  //       if (file.type === 'application/pdf') {
  //         setIsProcessingPdf(true);
  //         const imageDataUrl = await pdfToImage(file);
  //         setImage(imageDataUrl);
  //       } else if (file.type.startsWith('image/')) {
  //         const reader = new FileReader();
  //         reader.onloadend = () => {
  //           setImage(reader.result as string);
  //         };
  //         reader.readAsDataURL(file);
  //       } else {
  //         alert('Please upload a valid image or PDF file.');
  //       }
  //     } catch (error) {
  //       console.error('Error processing file:', error);
  //       alert('Failed to process the uploaded file.');
  //     } finally {
  //       setIsProcessingPdf(false);
  //       // Reset the input so the same file can be selected again if needed
  //       e.target.value = '';
  //     }
  //   }
  // };

  const handleAuthenticatedGeneration = async () => {
    // ... (keep handleAuthenticatedGeneration same)
    if (!prompt.trim()) {
      alert("Please enter a description for your design.");
      return;
    }
    
    // Validate prompt first
    setIsGenerating(true);
    const token = await getValidAccessToken();

    try {
      const response = await fetch(`${API_URL}/projects/plans/validate-prompt/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt: prompt }),
      });

      if (!response.ok) {
        if (response.status === 400) {
          try {
            const errorData = await response.json();
            setValidationError(errorData.error || 'Your prompt doesn\'t appear to be related to floor plan generation.');
            setValidationSuggestions(errorData.suggestions || []);
            setShowValidationModal(true);
            setIsGenerating(false);
            return;
          } catch (jsonError) {
            const errText = await response.text();
            throw new Error(`Backend error (${response.status}): ${errText}`);
          }
        }
        const errText = await response.text();
        throw new Error(`Backend error (${response.status}): ${errText}`);
      }

      // Prompt is valid, navigate to questionnaire page
      navigate("/generation-questionnaire", {
        state: {
          prompt: prompt
        }
      });

    } catch (error) {
      console.error("Error validating prompt:", error);
      alert("Failed to validate prompt. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGetStarted = () => {
    // ... (keep handleGetStarted same)
    const isAuthenticated = localStorage.getItem("access_token") !== null;

    if (isAuthenticated) {
      handleAuthenticatedGeneration();
    } else {
      // Save state before redirecting
      if (prompt) localStorage.setItem("pending_prompt", prompt);
      // if (image) localStorage.setItem("pending_image", image);

      navigate('/LoginPage?redirect=/');
    }
  };

  const features = [
    {
      icon: <Layers size={24} />,
      title: 'Real-Time Project',
      subtitle: 'Collaboration.',
      color: 'blue'
    },
    {
      icon: <Clipboard size={24} />,
      title: 'Prompt-to-Precision',
      subtitle: 'Planning.',
      color: 'orange'
    },
    {
      icon: <Box size={24} />,
      title: 'One-Click Realistic',
      subtitle: 'Rendering.',
      color: 'blue'
    },
    {
      icon: <Shield size={24} />,
      title: 'Client-Ready 3D',
      subtitle: 'Walkthroughs.',
      color: 'orange'
    }
  ];

  const faqs = [
    // ... (keep faqs same)
    {
      question: 'Q1. Is Sooru suitable for independent freelancers, small studios, or large firms?',
      answer: 'Sooru.AI is designed to be fully scalable. It empowers Independent Professionals with massive efficiency gains, while enabling large firms to enforce unified standards and facilitate seamless collaboration across complex, multi-disciplinary projects.'
    },
    {
      question: 'Q2. How can Sooru.AI help me increase the profitability and revenue of my projects?',
      answer: 'By automating manual work, reducing errors, and accelerating client approvals through instant visualization, Sooru.AI allows you to maximize billable hours, take on more projects, and improve overall margins without increasing headcount.'
    },
    {
      question: 'Q3. How does the real-time collaboration feature benefit architects, engineers, and clients?',
      answer: 'All stakeholders work in a unified digital workspace. This eliminates fragmented communication, allowing teams to review, comment, and approve design changes instantly, drastically accelerating decision cycles and project momentum.'
    },
    {
      question: 'Q4. Can I integrate Sooru.AI with my existing professional tools (e.g., AutoCAD, Revit, SketchUp)?',
      answer: 'Absolutely! Sooru.AI ensures your workflow remains seamless. All design outputs are compatible with industry-standard file formats (CAD, DXF, PDF, GLB, STL, and many more) for effortless export and seamless integration in your preferred software.'
    },
    {
      question: 'Q5. How does Sooru.AI ensure consistency across 2D plans, 3D models, and all discipline layers (MEP/Structural)?',
      answer: 'We maintain a Single Source of Truth. Any modification made in a 2D floorplan is instantly reflected in the 3D model and simultaneously updates the linked Structural and MEP layers, guaranteeing consistency and integrity across the entire project.'
    },
    {
      question: 'Q6. How does Sooru.AI automate the entire design and documentation process?',
      answer: 'Sooru.AI is an End-to-End Automation platform. After you input your design parameters, it instantly generates and syncs all necessary outputs, including architectural floor plans, 3D models, and complete construction document sets (Structural, Electrical, Plumbing, etc.)*'
    },
    // {
    //   question: 'Q7. What is the pricing structure? Do you offer a free trial or a flexible pay-per-project model?',
    //   answer: 'We offer a tiered subscription model designed to scale with your business, from independent freelancers to large studios. Yes, we offer a Starter tier or a trial period, allowing you to experience the platform core automation benefits before committing. Please contact info@sooru.ai for more information regarding this matter.'
    // }
  ];

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className={`home-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Loading Overlay */}
      {isGenerating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Generating Your Design</h2>
          <p style={{ fontSize: '16px', opacity: 0.8 }}>This may take a few moments...</p>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero">
        <video autoPlay loop muted playsInline className="hero-background-video">
          <source src="/vid1.webm" type="video/mp4" />
        </video>
        <div className="hero-content">
          <div className="hero-tagline">SYNTAX TO SKYLINES</div>
          <h1 className="hero-title">
            Transform Your <span className="highlight-orange">Architectural</span><br />
            <span className="highlight-orange">Planning</span> <span className="highlight-blue">with AI</span>
          </h1>
          <p className="hero-subtitle">
            Generate precise, customizable, and sustainable<br />
            architectural designs instantly
          </p>

          {/* Smart Prompt Container */}
          <div className="prompt-container">
            <div className="prompt-input-wrapper">
              <textarea
                className="prompt-input"
                placeholder={placeholderText + (charIndex === placeholders[placeholderIndex].length && !isDeleting ? '|' : '')}
                rows={2}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />

              {/* Image Preview */}
              {/* {image && (
                <div className="image-previews" style={{ display: 'flex', gap: '8px', paddingTop: '8px', overflowX: 'auto' }}>
                  <div className="image-preview-item" style={{ position: 'relative' }}>
                    <img src={image} alt="Preview" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '2px solid rgba(35, 59, 170, 0.2)' }} />
                    <button
                      onClick={() => setImage(null)}
                      className="image-preview-remove"
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        background: 'linear-gradient(135deg, #ff4444, #cc0000)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '12px',
                        border: '2px solid white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      âœ•
                    </button>
                  </div>
                </div>
              )} */}

              <div className="prompt-bottom-controls">
                <div className="prompt-left-controls">
                  <button
                    className="add-btn"
                    onClick={() => {
                      // Get or create a project ID for the upload
                      const token = localStorage.getItem('access_token');
                      if (!token) {
                        localStorage.setItem('pending_prompt', prompt);
                        // if (image) localStorage.setItem('pending_image', image);
                        navigate('/LoginPage');
                        return;
                      }
                      
                      // For now, we'll use a default project or create one
                      // You can modify this logic based on your needs
                      const getOrCreateProjectForUpload = async () => {
                        try {
                          const response = await fetch(`${API_URL}/projects/`, {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          
                          if (response.ok) {
                            const projects = await response.json();
                            if (projects && projects.length > 0) {
                              setCurrentProjectId(projects[0].id);
                              setShowUploadModal(true);
                            } else {
                              // Create a new project for upload
                              const createResponse = await fetch(`${API_URL}/projects/`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  project_name: `Upload Project - ${new Date().toLocaleDateString()}`
                                })
                              });
                              
                              if (createResponse.ok) {
                                const newProject = await createResponse.json();
                                setCurrentProjectId(newProject.id);
                                setShowUploadModal(true);
                              }
                            }
                          }
                        } catch (error) {
                          console.error('Error getting/creating project:', error);
                        }
                      };
                      
                      getOrCreateProjectForUpload();
                    }}
                    disabled={isProcessingPdf || prompt.trim().length > 0}
                    title={prompt.trim().length > 0 ? "Clear the prompt to upload" : "Upload Image/PDF"}
                  >
                    {isProcessingPdf ? (
                      <span>Processing...</span>
                    ) : (
                      <>
                        <svg 
                          width="20" 
                          height="20" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                        <span className="upload-btn-text">
                          <span className="upload-btn-full">Upload Drawings / Hand-Drawn Sketches</span>
                          <span className="upload-btn-short">Upload</span>
                        </span>
                      </>
                    )}
                  </button>
                </div>
                <button className="generate-btn1" onClick={handleGetStarted} disabled={isGenerating}>
                  <Lightbulb size={16} />
                  {isGenerating ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>

            {/* Quick Prompts Row */}
            <div className="quick-prompts-row">
              {quickPrompts.map((qp, idx) => (
                <button
                  key={idx}
                  className="quick-prompt-btn"
                  onClick={() => handleQuickPrompt(qp.prompts)}
                >
                  <span className="qp-icon">{qp.icon}</span>
                  <span className="qp-label">{qp.label}</span>
                </button>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Powered By Section */}
      <section className="powered-by-section">
        <h2 className="powered-by-heading">Powered By</h2>
        <div className="powered-by-logo">
          <img 
            src={isDarkMode ? "/images/DrawGPD White.png" : "/images/DrawGPD.png"} 
            alt="DrawGPD" 
            className="drawgpd-logo" 
          />
          {/* <p className="drawgpd-tagline">Generative Prompt Design</p> */}
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section">
        <h2 className="why-choose-heading">Why Choose Sooru.AI?</h2>
        <div className="why-choose-grid">
          <div className="why-choose-card">
            <div className="why-choose-icon">
              <Lightbulb size={40} />
            </div>
            <h3>Design 10x Faster</h3>
            <ul>
              <li>Instant Layouts</li>
              <li>Real-time 2D-3D Design</li>
              <li>End the Redraw Loop</li>
            </ul>
          </div>
          <div className="why-choose-card">
            <div className="why-choose-icon">
              <Clipboard size={40} />
            </div>
            <h3>Smart Editor</h3>
            <ul>
              <li>Easy Modifications</li>
              <li>Vast, Customizable Library</li>
              <li>Import/Export Options</li>
            </ul>
          </div>
          <div className="why-choose-card">
            <div className="why-choose-icon">
              <Users size={40} />
            </div>
            <h3>Engage Clients Instantly</h3>
            <ul>
              <li>Live 3D Walkthroughs</li>
              <li>Secure, Simple Sharing</li>
              <li>Accelerated Approvals</li>
            </ul>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <h2 className="faq-heading">Frequently Asked Questions (FAQs)</h2>

          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${activeIndex === index ? 'active' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFAQ(index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown className="faq-icon" size={20} />
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {showUploadModal && currentProjectId && (
        <UploadPlanModal
          projectId={currentProjectId}
          uploadPlan={showUploadModal}
          onClose={() => setShowUploadModal(false)}
        />
      )}
      <PromptValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        errorMessage={validationError}
        suggestions={validationSuggestions}
        theme="default"
      />
    </div>
  );
};

export default Home;